import React, { useContext, useEffect, useState } from 'react';
import { Col, Row, Select } from 'antd';
import Section from '../../components/section/section';
import MultyBarChart from '../../components/multy-bar-chart/multy-bar-chart';
import ChartCard from '../../components/chart-card/chart-card';
import MultyLineChart from '../../components/multy-line-chart/multy-line-chart';
import Context from '../../context/context';
import { months, ASPECT_TYPES, ESTABLISHMENT_TYPES } from '../../constants';
import AppliedFilters from '../../components/applied-filters/applied-filters';
import PageTitle from '../../components/page-title/page-title';
import { scrollToTop } from '../../utils/helpers';
import EstablishmentTypeToggle from '../../components/establishment-type-toggle/establishment-type-toggle';

import './categories.css';

const establishmentTypes = ESTABLISHMENT_TYPES.map((type) => ({
  label: type.charAt(0).toUpperCase() + type.slice(1).toLowerCase(),
  value: type,
}));

const Aspect = ({ aspect, value }) => {
  const { selectedAspect, updateSelectedAspect } = useContext(Context);

  const isTotal = aspect === 'Total';

  const handleClick = () => {
    if (isTotal) return;

    if (selectedAspect.includes(aspect)) {
      updateSelectedAspect(selectedAspect.filter((a) => a !== aspect));
    } else {
      updateSelectedAspect([...selectedAspect, aspect]);
    }
  };

  return (
    <div
      className={`aspect ${selectedAspect.includes(aspect) ? 'selected' : ''} ${
        isTotal ? 'disabled' : ''
      }`}
      onClick={isTotal ? undefined : handleClick} // Prevent click event for "Total"
    >
      <div className="aspect-top-container">
        <div className="aspect-icon-container">
          <img
            className="aspect-icon"
            src={`/icons/${aspect.toLowerCase()}.png`}
            alt={aspect}
          />
        </div>
        <div className="aspect-name">{aspect}</div>
      </div>
      <div className="aspect-value">
        <div className="aspect-value-counter">{value}</div>
        Mentions
      </div>
    </div>
  );
};

const Categories = (props) => {
  const {
    filteredReviews,
    selectedCategory,
    selectedPlatform,
    selectedSubCategory,
    selectedSentiment,
    selectedAspect,
    selectedRegion,
    updateSelectedCategory,
  } = useContext(Context);

  const [aspectsData, setAspectsData] = useState(computeAspectsData());
  const [sentimentTimeData, setSentimentTimeData] = useState(
    computeSentimentTimeData()
  );
  const [visitsOverYears, setVisitsOverYears] = useState(
    computeVisitsOverYears()
  );

  function computeAspectsData() {
    const aspectsDataMap = {};

    ASPECT_TYPES.forEach((aspect) => {
      aspectsDataMap[aspect] = 0;
    });

    filteredReviews.forEach((review) => {
      review.aspectsentiment_set.forEach((aspect) => {
        if (!selectedSentiment || aspect.label === selectedSentiment) {
          aspectsDataMap[aspect.aspect] += 1;
        }
      });
    });
    return aspectsDataMap;
  }

  function computeTotalAspects() {
    return Object.values(aspectsData).reduce((acc, value) => acc + value, 0);
  }

  function computeSentimentTimeData() {
    const sentimentArrays = filteredReviews.reduce(
      (sentimentAcc, review) => {
        const date = new Date(review.date);
        const monthKey = `${date.getFullYear()}-${date.getMonth() + 1}`;

        review.aspectsentiment_set.forEach((aspect) => {
          if (!selectedSentiment || aspect.label === selectedSentiment) {
            if (!sentimentAcc[aspect.label.toLowerCase()]) {
              sentimentAcc[aspect.label.toLowerCase()] = {};
            }

            sentimentAcc[aspect.label.toLowerCase()][monthKey] =
              (sentimentAcc[aspect.label.toLowerCase()][monthKey] || 0) + 1;
          }
        });
        return sentimentAcc;
      },
      { positive: {}, negative: {} }
    );

    const sentimentArrayMerged = [];
    const allMonths = new Set([
      ...Object.keys(sentimentArrays.positive),
      ...Object.keys(sentimentArrays.negative),
    ]);

    allMonths.forEach((monthKey) => {
      const [year, month] = monthKey.split('-');
      const date = new Date(year, month - 1);

      sentimentArrayMerged.push({
        key: date,
        group: 'Positive',
        value: sentimentArrays.positive[monthKey] || 0,
      });
      sentimentArrayMerged.push({
        key: date,
        group: 'Negative',
        value: sentimentArrays.negative[monthKey] || 0,
      });
    });

    return sentimentArrayMerged.sort((a, b) => a.key - b.key);
  }

  function xTicksFormatter(value) {
    const options = { year: '2-digit', month: 'short' };
    return value.toLocaleString('en-US', options);
  }

  function computeVisitsOverYears() {
    const yearMap = {};
    filteredReviews.forEach((review) => {
      const year = new Date(review.date).getFullYear();
      if (!yearMap[year]) {
        yearMap[year] = {
          reviews: 0,
          negative: 0,
          positive: 0,
        };
      }

      yearMap[year].reviews += 1;

      review.aspectsentiment_set.forEach((aspect) => {
        if (!selectedSentiment || aspect.label === selectedSentiment) {
          if (aspect.label === 'Positive') {
            yearMap[year].positive += 1;
          } else if (aspect.label === 'Negative') {
            yearMap[year].negative += 1;
          }
        }
      });
    });

    const res = Object.entries(yearMap).map(([year, yearData]) => {
      const result = { group: year };
      Object.entries(yearData).forEach(([key, value]) => {
        result[key] = value;
      });
      return result;
    });

    res.columns = ['group', 'positive', 'reviews', 'negative'];
    return res;
  }

  useEffect(() => {
    scrollToTop();
  }, []);

  useEffect(() => {
    setSentimentTimeData(computeSentimentTimeData());
    setVisitsOverYears(computeVisitsOverYears());
    setAspectsData(computeAspectsData());
  }, [filteredReviews, selectedSentiment]);

  const appliedFilters = {
    platform: selectedPlatform,
    category: selectedCategory,
    subcategories: selectedSubCategory,
    sentiment: selectedSentiment,
    aspects: selectedAspect,
    regions: selectedRegion,
  };

  return (
    <>
      <Row>
        <Col span={24}>
          <PageTitle
            title="Categories Overview"
            // subtitle="A comprehensive overview of aspects, sentiments, and reviews"
          />
        </Col>
      </Row>

      <Row>
        <Col span={24} className="categories-filters">
          <AppliedFilters />
        </Col>
      </Row>
      <div style={{ marginTop: '10px', marginBottom: '20px' }}>
        <EstablishmentTypeToggle />
      </div>
      <div className="categories-wrapper">
        <div className="overview-block aspects">
          {ASPECT_TYPES.map((aspect) => (
            <Aspect key={aspect} aspect={aspect} value={aspectsData[aspect]} />
          ))}
          <Aspect aspect="Total" value={computeTotalAspects()} />
        </div>

        <div className="categories-charts">
          <Section
            title={
              <span>Trends in Positive and Negative Sentiments Over Time</span>
            }
          >
            <ChartCard>
              <MultyLineChart
                id="sentiment-over-time"
                xTicksFormatter={xTicksFormatter}
                data={sentimentTimeData}
                colors={['#6FCF97', '#EB5757']}
                columns={{ key: 'Date', group: 'Type', value: 'Value' }}
                fileName="sentiments-over-time"
                filters={appliedFilters}
                title={'Positive and negative sentiments over time'}
                hasLegend={true}
              />
            </ChartCard>
          </Section>

          <Section title={<span>Visitor Reviews Across Different Years</span>}>
            <ChartCard>
              <MultyBarChart
                id="visits-over-years"
                data={visitsOverYears}
                columns={{
                  group: 'Year',
                  reviews: 'Reviews',
                  positive: 'Positive',
                  negative: 'Negative',
                }}
                fileName="reviews-over-years"
                filters={appliedFilters}
                title={'Number of reviews based on years'}
              />
            </ChartCard>
          </Section>
        </div>
      </div>
    </>
  );
};

Categories.propTypes = {};

Categories.defaultProps = {};

export default Categories;
